import type { ReactElement } from 'react';

import { useTranslation } from '@govard/i18n-core/react';
import { AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';

import { WidgetContainer, type WidgetContainerProps } from './WidgetContainer.js';
import type { DashboardItemStatus, FocusPriority } from './types.js';

export interface FocusItem {
  id: string;
  title: string;
  subtitle?: string;
  status: DashboardItemStatus;
  priority?: FocusPriority;
  actionLabel?: string;
  targetUrl?: string;
  prompt?: string;
  /** Deal amount behind the row (prototype: ₽ column); null for non-deal rows. */
  amount?: number | null;
  currency?: string | null;
  /** Last real contact timestamp (prototype meta: «контакт 26 сен»). */
  lastActivityAt?: string | null;
}

export interface FocusTodayWidgetProps {
  items?: readonly FocusItem[];
  className?: string;
  testId?: string;
  loading?: boolean;
  onViewAll?: () => void;
  onItemClick?: (item: FocusItem) => void;
}

type RiskLevel = 'high' | 'mid' | 'low';

function formatFocusMoney(amount: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale === 'en' ? 'en-US' : 'ru-RU', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount} ${currency}`;
  }
}

function riskLevel(priority: FocusPriority | undefined): RiskLevel {
  switch (priority) {
    case 'P0':
      return 'high';
    case 'P1':
      return 'mid';
    default:
      return 'low';
  }
}

/** Spec D `.badge` — risk pill with a soft tinted surface and hairline ring. */
const RISK_PILL_STYLES: Record<RiskLevel, { color: string; background: string; border: string }> = {
  high: {
    // Semantic status tokens (not --ant-*): antd's cssVar scope overrides the
    // latter inside its subtree with a muted dark value that fails AA.
    color: 'var(--color-error, #b91c1c)',
    background: 'var(--ant-color-error-bg, #fef2f2)',
    border: 'color-mix(in srgb, var(--color-error, #b91c1c) 32%, transparent)',
  },
  mid: {
    color: 'var(--color-warning, #c2410c)',
    background: 'var(--ant-color-warning-bg, #fff7ed)',
    border: 'color-mix(in srgb, var(--color-warning, #c2410c) 32%, transparent)',
  },
  low: {
    color: 'var(--ant-color-text-tertiary, #6d6963)',
    background: 'var(--ant-color-bg-layout, #f9f7f4)',
    border: 'transparent',
  },
};

/** Spec D status dots: 6px; the high-risk dot pulses. */
const RISK_DOT_STYLES: Record<RiskLevel, { background: string; pulse: boolean }> = {
  high: { background: 'var(--ant-color-error, #b91c1c)', pulse: true },
  mid: { background: 'var(--ant-color-primary-border-hover, #d97706)', pulse: false },
  low: { background: 'var(--ant-color-border, #d6d3d1)', pulse: false },
};

export function FocusTodayWidget({
  items = [],
  className,
  testId,
  loading = false,
  onViewAll,
  onItemClick,
}: FocusTodayWidgetProps): ReactElement {
  const { t, locale } = useTranslation();
  const canViewAll = typeof onViewAll === 'function';
  const widgetContainerProps = {
    title: t('crm.dashboard.focus.title'),
    icon: AlertTriangle,
    iconColor: 'var(--ant-color-error, #b91c1c)',
    minHeight: 200,
    noPadding: true,
    ...(className ? { className } : {}),
    ...(testId ? { testId } : {}),
    headerRight: (
      <span className="flex items-center gap-2">
        {items.length > 0 ? (
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 550,
              whiteSpace: 'nowrap',
              color: 'var(--ant-color-primary-text, #b45309)',
              border:
                '1px solid color-mix(in srgb, var(--ant-color-primary-border-hover, #d97706) 45%, transparent)',
              background:
                'color-mix(in srgb, var(--ant-color-primary-bg, #fffbeb) 80%, transparent)',
              borderRadius: 999,
              padding: '0 8px',
              lineHeight: '17px',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {items.length}
          </span>
        ) : null}
        <button
          type="button"
          {...(canViewAll ? { onClick: onViewAll } : {})}
          disabled={!canViewAll}
          className="flex items-center font-medium"
          style={{
            fontSize: 11,
            color: canViewAll
              ? 'var(--ant-color-primary-text, #b45309)'
              : 'var(--ant-color-text-tertiary, #6d6963)',
            gap: 4,
            cursor: canViewAll ? 'pointer' : 'default',
            transition: 'opacity 110ms ease',
          }}
        >
          {t('crm.dashboard.focus.view_all')} <ExternalLink size={10} />
        </button>
      </span>
    ),
  } satisfies Omit<WidgetContainerProps, 'children'>;

  return (
    <WidgetContainer {...widgetContainerProps}>
      {loading ? (
        <div
          className="animate-pulse"
          style={{ padding: '8px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}
        >
          {[1, 2, 3].map((index) => (
            <div
              key={index}
              style={{
                height: 42,
                borderRadius: 'var(--ant-border-radius-sm, 6px)',
                background: 'var(--g-fill, #f5f1eb)',
              }}
            />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div
          className="flex items-center justify-center gap-2"
          style={{ padding: '18px 16px', opacity: 0.6 }}
        >
          <CheckCircle2 size={16} style={{ color: 'var(--ant-color-success, #15803d)' }} />
          <span
            style={{
              fontSize: 12,
              color: 'var(--ant-color-text-tertiary, #6d6963)',
            }}
          >
            {t('crm.dashboard.focus.empty')}
          </span>
        </div>
      ) : (
        <div className="crm-att-list" data-testid="dashboard-focus-rows">
          {items.map((item) => {
            const level = riskLevel(item.priority);
            const pill = RISK_PILL_STYLES[level];
            const dot = RISK_DOT_STYLES[level];
            // Prototype meta: «контакт 26 сен» — the real last-contact date;
            // rows without a date fall back to the action label.
            const metaLabel =
              item.lastActivityAt !== undefined && item.lastActivityAt !== null
                ? `${t('crm.dashboard.focus.last_contact')} ${new Intl.DateTimeFormat(
                    locale === 'en' ? 'en-US' : 'ru-RU',
                    { day: 'numeric', month: 'short' },
                  ).format(new Date(item.lastActivityAt))}`
                : (item.actionLabel ?? '');

            return (
              <button
                key={item.id}
                type="button"
                {...(onItemClick ? { onClick: () => onItemClick(item) } : {})}
                disabled={!onItemClick}
                className="crm-att-row"
                style={{ cursor: onItemClick ? 'pointer' : 'default' }}
              >
                <span className="crm-att-who flex min-w-0 items-baseline gap-2">
                  <span
                    className={dot.pulse ? 'crm-dot-pulse' : undefined}
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      background: dot.background,
                      flexShrink: 0,
                      alignSelf: 'center',
                    }}
                  />
                  <span
                    className="truncate"
                    style={{
                      fontSize: 12.5,
                      fontWeight: 550,
                      color: 'var(--ant-color-text, #292524)',
                      lineHeight: 1.3,
                    }}
                  >
                    {item.title}
                  </span>
                </span>
                <span
                  className="crm-att-subject truncate"
                  style={{
                    fontSize: 12,
                    color: 'var(--ant-color-text-secondary, #57534e)',
                    lineHeight: 1.3,
                  }}
                >
                  {item.subtitle ?? t('crm.dashboard.focus.no_project')}
                </span>
                {item.amount !== undefined && item.amount !== null ? (
                  <span
                    className="crm-att-amount truncate"
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                      fontSize: 12,
                      fontWeight: 550,
                      color: 'var(--ant-color-text, #292524)',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.3,
                    }}
                  >
                    {formatFocusMoney(item.amount, item.currency ?? 'RUB', locale)}
                  </span>
                ) : (
                  <span className="crm-att-amount" />
                )}
                <span
                  className="crm-att-badge"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    padding: '1px 7px',
                    borderRadius: 999,
                    border: `1px solid ${pill.border}`,
                    background: pill.background,
                    color: pill.color,
                    whiteSpace: 'nowrap',
                    justifySelf: 'start',
                  }}
                >
                  {t(`crm.dashboard.focus.priority.${level}`)}
                </span>
                {metaLabel ? (
                  <span
                    className="crm-att-meta truncate"
                    style={{
                      fontFamily:
                        'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
                      fontSize: 11,
                      color: 'var(--ant-color-text-tertiary, #6d6963)',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      lineHeight: 1.3,
                    }}
                  >
                    {metaLabel}
                  </span>
                ) : (
                  <span className="crm-att-meta" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </WidgetContainer>
  );
}
